<script lang="ts" setup>
import { useCartStore } from '@/stores/cart/useCartStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { useRoute } from '#imports';
import useEVMApi from '~/composables/useApi/useEVMApi';
import type { ApiDirection, ApiPagination, NftListQuery, NftListSort } from '~/service/api/types';

const {
    QueryWTFApiKey,
    collection,
    nft
} = useWTFApi();
const route = useRoute();

const { utils: { isEvmErc1155 } } = useProviderConfig()(['utils']);

const slug = route?.params?.slug as string;

const isOpenFilter = ref<boolean>(true);

const tabs = [
    {
        value: 'Items',
        label: 'Items'
    }
];

const activeTab = ref<typeof tabs[number]['value']>('Items');
const changeGrid = ref<boolean>(false);
const isMounted = ref(false);

const userStore = useUserStore();

const cartStore = useCartStore();

const { user } = storeToRefs(userStore);

const { cart } = storeToRefs(cartStore);

const {
    'data': collectionProfile,
    'status': collectionProfileStatus
} = useAsyncData(
    `${QueryWTFApiKey.COLLECTION}-${slug}`,
    async () => {
        const response = await collection.collectionGet(slug || '');
        return response.data;
    }
);

if (collectionProfile.value) {
    useMetaCollectionSlug({
        collectionProfile: collectionProfile.value,
        route
    });
}
const currencyOptions = computed(() => {
    return [
        {
            value: 'usd',
            label: 'USD'
        }
    ];
});

type RouteStateType =
    Pick<NftListQuery, 'search'> &
    Pick<ApiPagination, 'page' | 'size'> &
    {
        minPrice?: string;
        maxPrice?: string;
        sort: NftListSort;
        direction: ApiDirection;
        listed: string;
    };

const {
    onChangeQueryParams,
    query,
    size,
    totalPgn,
    guardQueryParams
} = useWatchQueryParams<RouteStateType>({
    search: '',
    listed: 'false',
    minPrice: '',
    maxPrice: '',
    page: 1,
    size: 20,
    sort: 'price',
    direction: 'asc'
}, `/collections/${route.params.slug}`);

const { nft: nftMarketApi } = useEVMApi();

const {
    'data': nftData,
    'status': nftDataStatus,
    'error': errorNFTData
} = useAsyncData(
    `${QueryWTFApiKey.NFT_LIST}-${route.params.slug}-${query.value.page}`,
    async () => {
        const data = await nftMarketApi.getNftList(
            slug || '',
            {
                ...query.value,
                listed: query.value.listed === 'true',
                ...(user ? { owner: user.value } : {}),
                maxPrice: query.value.maxPrice
                    ? parseFloat(query.value.maxPrice)
                    : undefined,
                minPrice: query.value.minPrice
                    ? parseFloat(query.value.minPrice)
                    : undefined
            }
        );
        await guardQueryParams(data.pagination);
        return data;
    },
    {
        watch: [query, user]
    }
);

// todo 'collection' отсутствует в wtf api
// todo 'owner' отсутствует в wtf api
// todo 'marketplace' отсутствует в wtf api
const offerNftData = ref<Nullable<Pick<AppWTFNft, 'name' | 'uuid' | 'address' | 'price' | 'network' | 'identifier' | 'displayImageURL' | 'itemType'>>>(null);
const listingNftData = ref<Nullable<Pick<AppWTFNft, 'name' | 'uuid' | 'ownedCount' | 'address' | 'price' | 'network' | 'identifier' | 'displayImageURL' | 'itemType'> & {
    collectionName: string;
}>>();

const isModalMakeOfferOpen = ref(false);
const isListingModalOpen = ref(false);

const openModal = (index: number, type: 'listing' | 'offer') => {
    if (userStore.user && nftData.value?.data.length && nftData.value?.data[index] && type === 'offer') {
        offerNftData.value = {
            // todo 'marketplace' отсутствует в wtf api
            //   marketplace: 'default',
            name: nftData.value.data[index].name,
            price: nftData.value.data[index].price,
            network: nftData.value.data[index].network,
            address: nftData.value.data[index].address,
            uuid: nftData.value.data[index].uuid,
            displayImageURL: nftData.value.data[index].displayImageURL,
            identifier: nftData.value.data[index].identifier,
            itemType: nftData.value.data[index].itemType
        };
        isModalMakeOfferOpen.value = true;
    }
    else if (userStore.user && nftData.value?.data.length && nftData.value?.data[index] && type === 'listing') {
        listingNftData.value = {
            name: nftData.value.data[index].name,
            network: nftData.value.data[index].network,
            address: nftData.value.data[index].address,
            identifier: nftData.value.data[index].identifier,
            displayImageURL: nftData.value.data[index].displayImageURL,
            // todo 'marketplace' отсутствует в wtf api
            //   marketplace: 'default',
            price: nftData.value.data[index].price,
            // todo 'owner' ждём пока добавят в api
            //   owner: nftData.value.content[index].owner,
            uuid: nftData.value.data[index].uuid,
            collectionName: collectionProfile.value?.name || '',
            itemType: nftData.value.data[index].itemType,
            ownedCount: nftData.value.data[index].ownedCount
        };
        isListingModalOpen.value = true;
    }
    else {
        userStore.showUserIsDisconnect();
    }
};

const resetFilters = () => {
    onChangeQueryParams({
        search: '',
        listed: 'false',
        minPrice: '',
        maxPrice: '',
        page: 1,
        sort: 'price',
        direction: 'asc'
    });
};

onMounted(() => {
    isMounted.value = true;
});

</script>

<template>
  <div>
    <Breadcrumbs :currentPosition="false"
                 :data="{
                   name:collectionProfile?.name || 'Loading...',
                   slug:route.params.slug as string
                 }"
    />
    <section aria-label="collection info">
      <div class="display-flex flex-column width-full">
        <CollectionInfo :collection-info="collectionProfile"
                        :is-loading="collectionProfileStatus === 'pending'"
        />

        <Tabs :active-tab="activeTab"
              :tabs="tabs"
        />
      </div>
    </section>

    <section aria-label="main content">
      <div :class="'display-flex justify-start width-full'"
      >
        <Suspense>
          <template #default>
            <FiltersPanel v-model:model-value="isOpenFilter">
              <FilterRangeValues :is-loading="collectionProfileStatus === 'pending'"
                                 :select-items="currencyOptions"
                                 :select-value="{ max: query?.maxPrice, min: query?.minPrice }"
                                 title="Price"
                                 type="select"
                                 @on-change="({ min, max }) => onChangeQueryParams({ minPrice: min, maxPrice: max, page: 1 })"
              />
              <div class="display-flex blocks flex-column pad-24 gap-12">
                <div class="display-flex flex-column gap-8">
                  <div class="display-flex align-items-center justify-between width-full">
                    <p class="compact-500-accent">
                      Buy now
                    </p>
                    <Checkbox :checked="query.listed === 'true'"
                              label="buy now"
                              @on-change="listed => onChangeQueryParams({ listed: `${listed}`, page: 1 })"
                    />
                  </div>
                </div>
              </div>

              <LazySearchCollectionsList />
            </FiltersPanel>
          </template>
          <template #fallback>
            <div class="display-flex gap-8 container-width flex-column modal-style-768">
              <LoaderSkeleton :height="132"
                              class="align-self-stretch" />
              <LoaderSkeleton :height="132"
                              class="align-self-stretch" />
            </div>
          </template>
        </Suspense>

        <div class="display-flex flex-column width-full gap-16 justify-between">
          <div class="gap-16 display-flex flex-column grow">
            <SearchBar v-model="isOpenFilter"
                       v-model:change-grid="changeGrid"
                       :selected-direction="query.direction"
                       type-view="collection"
                       @on-change-direction="
                         (val) => onChangeQueryParams({ direction: val })
                       "
                       @on-change-search="val => onChangeQueryParams({ search: val, page: 1 })"
            />
            <SeoHeading :text="`Explore NFTs from the ${collectionProfile?.name} Collection`"
                        tag="h2"
            />
            <GridLayout :change-grid="changeGrid"
                        :data="nftData?.data"
                        :is-error="nftDataStatus === 'error'"
                        :is-loading="nftDataStatus === 'pending'"
                        :loader-size="+size"
            >
              <template #loader>
                <LoaderSkeleton class="nft-skeleton" />
              </template>
              <template #cart="{ item, index }">
                <Suspense>
                  <template #fallback>
                    <LoaderSkeleton class="nft-skeleton" />
                  </template>
                  <LazyNftCard :key="item.uuid"
                               v-model:model-value="isModalMakeOfferOpen"
                               :address="item.address"
                               :animImg="item.displayAnimationURL"
                               :collectionSlug="item.collection.slug"
                               :grid-change="changeGrid"
                               :identifier="item.identifier"
                               :img="item.displayImageURL"
                               :is-exist-to-cart="isMounted && cart.has(item.uuid)"
                               :itemType="item.itemType"
                               :network="item.network"
                               :ownedCount="item.ownedCount"
                               :price="item.price"
                               :title="item.name || item.identifier"
                               :uuid="item.uuid"
                               sizes="286px xs:254px sm:282px md:220px lg:181px xl:165px xxl:169px"
                               @make-offer="() => openModal(index, 'offer')"
                               @open-listing="() => openModal(index, 'listing')"
                  />
                </Suspense>
              </template>
              <template #empty>
                <div class="display-flex align-items-center grow flex-column justify-center">
                  <span class="text-center mb-8">Nothing found, want to reset filters?</span>
                  <Button :color="'bg.mojito'"
                          :size="'size.medium'"
                          @click="resetFilters"
                  >
                    <button class="compact-500-accent"
                            type="button"
                    >
                      Reset
                    </button>
                  </Button>
                </div>
              </template>
              <template #error>
                <span>{{ errorNFTData?.message }}</span>
              </template>
            </GridLayout>
          </div>
          <Suspense>
            <LazyPagination :page="+query.page"
                            :size="+size"
                            :total="totalPgn"
                            :visible-items="3"
                            @on-page="(page: number) => onChangeQueryParams({ page })"
            />
            <template #fallback>
              <div class="display-flex flex-row align-items-center paginate gap-8">
                <LoaderSkeleton :height="30"
                                :width="30" />
                <LoaderSkeleton :height="30"
                                :width="30" />
                <LoaderSkeleton :height="30"
                                :width="30" />
              </div>
            </template>
          </Suspense>
        </div>
      </div>
    </section>
    <ClientOnly>
      <Teleport to="body">
        <ModalCartNetworkDifference />
        <Modal v-model:model-value="isModalMakeOfferOpen">
          <ModalMakeOffer v-if="offerNftData && collectionProfile"
                          v-model:model-value="isModalMakeOfferOpen"
                          :data="{
                            name: offerNftData.name,
                            address: offerNftData.address,
                            slug,
                            network: offerNftData.network,
                            identifier: offerNftData.identifier,
                            displayImageURL: offerNftData.displayImageURL,
                            price: offerNftData.price,
                            uuid: offerNftData.uuid,
                            itemType: offerNftData.itemType
                          }"
                          :fees="collectionProfile.fees"
          />
        </Modal>
        <Modal v-model:model-value="isListingModalOpen"
               class="token-modal__listing"
        >
          <ModalListing v-if="listingNftData"
                        v-model:model-value="isListingModalOpen"
                        :data="{
                          name: listingNftData.name,
                          address: listingNftData.address,
                          network: listingNftData.network,
                          identifier: listingNftData.identifier,
                          displayImageURL: listingNftData.displayImageURL,
                          price: listingNftData.price,
                          slug,
                          uuid: listingNftData.uuid,
                          collectionName: collectionProfile?.name,
                          itemType: listingNftData.itemType,
                          ownedCount: listingNftData.ownedCount
                        }"
          />
        </Modal>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
:deep(.nft-skeleton) {
  height: 270px;
}

:deep(.token-modal__listing) {
  max-height: 100%;
}

.blocks {
  width: 100%;
  border-radius: var(--background-500-radius, 4px);
  background: var(--layer-floor-1);

  @include tablet-768 {
    background: var(--layer-floor-2);
  }
}

.test-layout-card {
  display: grid;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(182px, 1fr));
  grid-template-rows: auto;
}
</style>
